import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck, Sparkles } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { formatCOP } from "../data/products";
import { useState } from "react";
import { CheckoutDialog } from "./CheckoutDialog";

const FREE_SHIPPING_THRESHOLD = 200000;

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    updateQty,
    removeItem,
    subtotal,
    count,
  } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleCheckout = () => {
    setCheckoutOpen(true);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(o) => (o ? null : closeCart())}>
        <SheetContent
          side="right"
          data-testid="cart-drawer"
          aria-describedby={undefined}
          className="w-full sm:max-w-md flex flex-col bg-slate-50 p-0 border-l border-slate-200"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
            <SheetTitle className="font-serif text-2xl text-slate-900 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-[#E85A3C]" strokeWidth={1.75} />
              Tu carrito
              {count > 0 && (
                <span className="ml-1 text-sm text-slate-500 font-sans font-normal">
                  ({count} {count === 1 ? "pieza" : "piezas"})
                </span>
              )}
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div
              data-testid="cart-empty"
              className="flex-1 flex flex-col items-center justify-center text-center px-8"
            >
              <div className="h-16 w-16 rounded-full bg-slate-200/60 flex items-center justify-center mb-5">
                <ShoppingBag className="h-7 w-7 text-slate-400" strokeWidth={1.5} />
              </div>
              <p className="font-serif text-xl text-slate-900 mb-2">
                Aún no hay piezas
              </p>
              <p className="text-sm text-slate-500 max-w-xs">
                Explora la colección y empieza a armar tu galería.
              </p>
              <Button
                onClick={closeCart}
                data-testid="cart-empty-continue"
                className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
              >
                Seguir explorando
              </Button>
            </div>
          ) : (
            <>
              <div
                data-testid="cart-items"
                className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
              >
                {items.map((item) => (
                  <div
                    key={item.key}
                    data-testid={`cart-line-${item.key}`}
                    className="flex gap-4 bg-white rounded-xl p-3 border border-slate-100"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-24 w-20 object-cover rounded-lg bg-slate-100 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-serif text-base text-slate-900 leading-snug line-clamp-2">
                          {item.title}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeItem(item.key)}
                          data-testid={`cart-remove-${item.key}`}
                          aria-label="Eliminar"
                          className="text-slate-400 hover:text-[#E85A3C] transition-colors p-1 -mr-1 -mt-1"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {item.sizeLabel} · {item.frameLabel}
                      </p>

                      <div className="mt-auto pt-2 flex items-center justify-between">
                        <div className="inline-flex items-center bg-slate-100 rounded-full">
                          <button
                            type="button"
                            onClick={() => updateQty(item.key, item.qty - 1)}
                            data-testid={`cart-decr-${item.key}`}
                            aria-label="Restar"
                            className="h-7 w-7 flex items-center justify-center text-slate-600 hover:text-slate-900"
                          >
                            <Minus className="h-3 w-3" strokeWidth={2.5} />
                          </button>
                          <span
                            data-testid={`cart-qty-${item.key}`}
                            className="text-sm font-medium text-slate-900 w-6 text-center tabular-nums"
                          >
                            {item.qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQty(item.key, item.qty + 1)}
                            data-testid={`cart-incr-${item.key}`}
                            aria-label="Sumar"
                            className="h-7 w-7 flex items-center justify-center text-slate-600 hover:text-slate-900"
                          >
                            <Plus className="h-3 w-3" strokeWidth={2.5} />
                          </button>
                        </div>
                        <span className="text-sm font-semibold text-slate-900 tabular-nums">
                          {formatCOP(item.unitPrice * item.qty)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-200 px-6 py-5 bg-white space-y-4">
                {/* Free-shipping upsell */}
                {(() => {
                  const reached = subtotal >= FREE_SHIPPING_THRESHOLD;
                  const remaining = Math.max(
                    0,
                    FREE_SHIPPING_THRESHOLD - subtotal
                  );
                  const progress = Math.min(
                    100,
                    Math.round((subtotal / FREE_SHIPPING_THRESHOLD) * 100)
                  );
                  return (
                    <div
                      data-testid="cart-upsell"
                      className={`rounded-xl border px-3.5 py-3 ${
                        reached
                          ? "bg-[#C8D5C0]/30 border-[#C8D5C0]"
                          : "bg-[#E85A3C]/5 border-[#E85A3C]/20"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        {reached ? (
                          <Sparkles
                            className="h-4 w-4 mt-0.5 text-emerald-700 flex-shrink-0"
                            strokeWidth={2}
                          />
                        ) : (
                          <Truck
                            className="h-4 w-4 mt-0.5 text-[#E85A3C] flex-shrink-0"
                            strokeWidth={2}
                          />
                        )}
                        <p
                          data-testid="cart-upsell-message"
                          className="text-xs leading-snug text-slate-700"
                        >
                          {reached ? (
                            <>
                              <span className="font-semibold text-emerald-800">
                                ¡Envío gratis activado!
                              </span>{" "}
                              Tu pedido viaja sin costo a toda Colombia.
                            </>
                          ) : (
                            <>
                              Añade{" "}
                              <span className="font-semibold text-slate-900 tabular-nums">
                                {formatCOP(remaining)}
                              </span>{" "}
                              más y tu envío es{" "}
                              <span className="font-semibold text-[#E85A3C]">
                                gratis
                              </span>
                              .
                            </>
                          )}
                        </p>
                      </div>
                      <div
                        className="mt-2.5 h-1.5 rounded-full bg-slate-200/70 overflow-hidden"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          data-testid="cart-upsell-progress"
                          className={`h-full rounded-full transition-all duration-500 ease-out ${
                            reached ? "bg-emerald-600" : "bg-[#E85A3C]"
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Subtotal</span>
                  <span
                    data-testid="cart-subtotal"
                    className="font-serif text-2xl text-slate-900 tabular-nums"
                  >
                    {formatCOP(subtotal)}
                  </span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Envío y descuentos se calculan en el siguiente paso.
                </p>
                <Button
                  onClick={handleCheckout}
                  data-testid="cart-checkout-button"
                  className="w-full bg-[#E85A3C] hover:bg-[#D3492D] text-white rounded-full h-12 text-base font-medium shadow-lg shadow-[#E85A3C]/30"
                >
                  Ir a pagar
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        onSuccess={() => {
          setCheckoutOpen(false);
          closeCart();
        }}
      />
    </>
  );
};
